import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { Alert, Autocomplete, Button, Chip, Link, MenuItem, ScrollableDrawer, Select, Spacer, Stack, TextField, Typography, useCurrentBreakpoint } from '@emburse/embark-core';
import { SupportIcon } from '@emburse/embark-icons';
import { Checkbox, CurrencySelect, IconPopover, EmployeeSearch, EmpUDFSearch } from '../../../components';
import ManageUsersLeaveModal from './ManageUsersLeaveModal';
import { defaultApprovalLimit, emailStatuses, adminLevels, empTypes } from '../../../interfaces/ManageUsers';
import { EmpUDF } from '../../../interfaces/UDF';
import { Employee, EmpType } from '../../../interfaces/Employee';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import useCurrencies from '../../../hooks/useCurrencies';
import { validateEmail } from '../../../util/formValidation';
const commuteUnitsSelect = [
    {
        label: 'Miles',
        value: false
    },
    {
        label: 'Kilometers',
        value: true
    }
];
function ManageUsersUserDrawer({ open, currencies, userData, departments, gld1, gld2, gld3, gld4, gld5, approvalLimits, hasBillingIncrease, onCancel, onSave, onApproverFor, onAccountantFor, closeDrawer, accountConfig, isTreasurerAvailable, settlementAccounts, expCatMaxOverride }) {
    useTranslation();
    const currentBreakpoint = useCurrentBreakpoint();
    const { getCurrencyByCode } = useCurrencies();
    const [validationResults, setValidationResults] = useState([]);
    const [openLeaveModal, setOpenLeaveModal] = useState(false);
    const [userEmail, setUserEmail] = useState({ value: userData?.UserEmail, validationText: '' });
    const [firstName, setFirstName] = useState({ value: userData?.FirstName, validationText: '' });
    const [lastName, setLastName] = useState({ value: userData?.LastName, validationText: '' });
    const [empID, setEmpID] = useState({ value: userData?.EmpID, validationText: '' });
    const [emailStatus, setEmailStatus] = useState({
        value: userData?.EmailStatus,
        validationText: ''
    });
    const [departmentName, setDepartmentName] = useState({
        value: userData?.DepartmentName,
        validationText: ''
    });
    const [departmentPK, setDepartmentPK] = useState({
        value: userData?.DepartmentPK,
        validationText: ''
    });
    const [commuteDistance, setCommuteDistance] = useState({
        value: userData?.CommuteDistance,
        validationText: ''
    });
    const [commuteUnits, setCommuteUnits] = useState({
        value: userData?.CommuteUnits,
        validationText: ''
    });
    const [adminLevel, setAdminLevel] = useState({
        value: userData?.AdminLevel,
        validationText: ''
    });
    const [role, setRole] = useState({ value: userData?.Role, validationText: '' });
    const [auditor, setAuditor] = useState({ value: userData?.Auditor, validationText: '' });
    const [treasurer, setTreasurer] = useState({ value: userData?.Treasurer, validationText: '' });
    const [translator, setTranslator] = useState({
        value: userData?.Translator,
        validationText: ''
    });
    const [accountsPayable, setAccountsPayable] = useState({
        value: userData?.AccountsPayable,
        validationText: ''
    });
    const [accountStatus, setAccountStatus] = useState({
        value: userData?.AccountStatus,
        validationText: ''
    });
    const [defaultCurrency, setDefaultCurrency] = useState({
        value: userData?.DefaultCurrency,
        validationText: ''
    });
    const [approverEmail, setApproverEmail] = useState({
        value: userData?.ApproverEmail,
        validationText: ''
    });
    const [approverPK, setApproverPK] = useState(userData?.ApproverPK);
    const [accountantEmail, setAccountantEmail] = useState({
        value: userData?.AccountantEmail,
        validationText: ''
    });
    const [accountantPK, setAccountantPK] = useState(userData?.AccountantPK);
    const [additionalApproverEmail, setAdditionalApproverEmail] = useState({
        value: userData?.AdditionalApproverEmail,
        validationText: ''
    });
    const [additionalApproverPK, setAdditionalApproverPK] = useState(userData?.AdditionalApproverPK);
    const [doNotSendWelcomeEmail, setDoNotSendWelcomeEmail] = useState(false);
    const [useMultipleCurrencies, setUseMultipleCurrencies] = useState({
        value: userData?.UseMultipleCurrencies,
        validationText: ''
    });
    const [useVAT, setUseVAT] = useState({ value: userData?.UseVAT, validationText: '' });
    const [defaultVATRate, setDefaultVATRate] = useState({
        value: userData?.DefaultVATRate,
        validationText: ''
    });
    const [usePST, setUsePST] = useState({ value: userData?.UsePST, validationText: '' });
    const [defaultPSTRate, setDefaultPSTRate] = useState({
        value: userData?.DefaultPSTRate,
        validationText: ''
    });
    const [useHST, setUseHST] = useState({ value: userData?.UseHST, validationText: '' });
    const [defaultHSTRate, setDefaultHSTRate] = useState({
        value: userData?.DefaultHSTRate,
        validationText: ''
    });
    const [empGLD1Name, setEmpGLD1Name] = useState(userData?.EmpGLD1Name);
    const [empGLD1PK, setEmpGLD1PK] = useState({ value: userData?.EmpGLD1PK, validationText: '' });
    const [empGLD2Name, setEmpGLD2Name] = useState(userData?.EmpGLD2Name);
    const [empGLD2PK, setEmpGLD2PK] = useState({ value: userData?.EmpGLD2PK, validationText: '' });
    const [empGLD3Name, setEmpGLD3Name] = useState(userData?.EmpGLD3Name);
    const [empGLD3PK, setEmpGLD3PK] = useState({ value: userData?.EmpGLD3PK, validationText: '' });
    const [empGLD4Name, setEmpGLD4Name] = useState(userData?.EmpGLD4Name);
    const [empGLD4PK, setEmpGLD4PK] = useState({ value: userData?.EmpGLD4PK, validationText: '' });
    const [empGLD5Name, setEmpGLD5Name] = useState(userData?.EmpGLD5Name);
    const [empGLD5PK, setEmpGLD5PK] = useState({ value: userData?.EmpGLD5PK, validationText: '' });
    const [approver, setApprover] = useState(new Employee({ PK: userData?.ApproverPK, Email: userData?.ApproverEmail }));
    const [accountant, setAccountant] = useState(new Employee({ PK: userData?.AccountantPK, Email: userData?.AccountantEmail }));
    const [additionalApprover, setAdditionalApprover] = useState(new Employee({
        PK: userData?.AdditionalApproverPK,
        Email: userData?.AdditionalApproverEmail
    }));
    const [approvalLimit, setApprovalLimit] = useState(userData?.ApprovalLimit || defaultApprovalLimit);
    const [administrativeNotes, setAdministrativeNotes] = useState(userData?.AdministrativeNotes || '');
    const [defaultSettlementAccount, setDefaultSettlementAccount] = useState(userData?.DefaultSettlementAccount || null);
    const [payByCheck, setPayByCheck] = useState(userData?.PayByCheck || false);
    const [maxOverrideLink, setMaxOverrideLink] = useState('');
    const defaultStateValues = useCallback(() => {
        setUserEmail({ value: userData?.UserEmail, validationText: '' });
        setFirstName({ value: userData?.FirstName, validationText: '' });
        setLastName({ value: userData?.LastName, validationText: '' });
        setEmpID({ value: userData?.EmpID, validationText: '' });
        setEmailStatus({ value: userData?.EmailStatus, validationText: '' });
        setDepartmentName({ value: userData?.DepartmentName, validationText: '' });
        setDepartmentPK({ value: userData?.DepartmentPK, validationText: '' });
        setCommuteDistance({ value: userData?.CommuteDistance, validationText: '' });
        setCommuteUnits({ value: userData?.CommuteUnits, validationText: '' });
        setAdminLevel({ value: userData?.AdminLevel, validationText: '' });
        setRole({ value: userData?.Role, validationText: '' });
        setAuditor({ value: userData?.Auditor, validationText: '' });
        setTreasurer({ value: userData?.Treasurer, validationText: '' });
        setTranslator({ value: userData?.Translator, validationText: '' });
        setAccountsPayable({ value: userData?.AccountsPayable, validationText: '' });
        setAccountStatus({ value: userData?.AccountStatus, validationText: '' });
        setDefaultCurrency({ value: userData?.DefaultCurrency, validationText: '' });
        setApproverEmail({ value: userData?.ApproverEmail, validationText: '' });
        setApproverPK(userData?.ApproverPK);
        setAccountantEmail({ value: userData?.AccountantEmail, validationText: '' });
        setAccountantPK(userData?.AccountantPK);
        setAdditionalApproverEmail({
            value: userData?.AdditionalApproverEmail,
            validationText: ''
        });
        setAdditionalApproverPK(userData?.AdditionalApproverPK);
        setDoNotSendWelcomeEmail(false);
        setUseMultipleCurrencies({ value: userData?.UseMultipleCurrencies, validationText: '' });
        setUseVAT({ value: userData?.UseVAT, validationText: '' });
        setDefaultVATRate({ value: userData?.DefaultVATRate, validationText: '' });
        setUsePST({ value: userData?.UsePST, validationText: '' });
        setDefaultPSTRate({ value: userData?.DefaultPSTRate, validationText: '' });
        setUseHST({ value: userData?.UseHST, validationText: '' });
        setDefaultHSTRate({ value: userData?.DefaultHSTRate, validationText: '' });
        setAdministrativeNotes(userData?.AdministrativeNotes || '');
        setDefaultSettlementAccount(userData?.DefaultSettlementAccount || null);
        setPayByCheck(userData?.PayByCheck || false);
        setEmpGLD1Name(userData?.EmpGLD1Name);
        setEmpGLD2Name(userData?.EmpGLD2Name);
        setEmpGLD3Name(userData?.EmpGLD3Name);
        setEmpGLD4Name(userData?.EmpGLD4Name);
        setEmpGLD5Name(userData?.EmpGLD5Name);
        setEmpGLD1PK({ value: userData?.EmpGLD1PK, validationText: '' });
        setEmpGLD2PK({ value: userData?.EmpGLD2PK, validationText: '' });
        setEmpGLD3PK({ value: userData?.EmpGLD3PK, validationText: '' });
        setEmpGLD4PK({ value: userData?.EmpGLD4PK, validationText: '' });
        setEmpGLD5PK({ value: userData?.EmpGLD5PK, validationText: '' });
        setApprover(new Employee({ PK: userData?.ApproverPK, Email: userData?.ApproverEmail }));
        setAccountant(new Employee({ PK: userData?.AccountantPK, Email: userData?.AccountantEmail }));
        setAdditionalApprover(new Employee({
            PK: userData?.AdditionalApproverPK,
            Email: userData?.AdditionalApproverEmail
        }));
        setApprovalLimit(userData?.ApprovalLimit || defaultApprovalLimit);
    }, [userData]);
    useEffect(() => {
        defaultStateValues();
    }, [defaultStateValues]);
    const fieldChangeHandler = (e, field) => {
        const fieldMap = {
            FirstName: setFirstName,
            LastName: setLastName,
            UserEmail: setUserEmail,
            EmpID: setEmpID,
            EmailStatus: setEmailStatus,
            CommuteDistance: setCommuteDistance,
            CommuteUnits: setCommuteUnits,
            AdminLevel: setAdminLevel,
            Role: setRole,
            Auditor: setAuditor,
            Treasurer: setTreasurer,
            Translator: setTranslator,
            AccountsPayable: setAccountsPayable,
            AccountStatus: setAccountStatus,
            DefaultCurrency: setDefaultCurrency,
            UseMultipleCurrencies: setUseMultipleCurrencies,
            UseVAT: setUseVAT,
            DefaultVATRate: setDefaultVATRate,
            UsePST: setUsePST,
            DefaultPSTRate: setDefaultPSTRate,
            UseHST: setUseHST,
            DefaultHSTRate: setDefaultHSTRate
        };
        if ((field.includes('Use') && !field.includes('User')) ||
            field.includes('Auditor') ||
            field.includes('Treasurer') ||
            field.includes('AccountsPayable') ||
            field.includes('Translator') ||
            field.includes('AccountStatus')) {
            fieldMap[field]((prevState) => ({ ...prevState, value: e.target.checked }));
        }
        else {
            fieldMap[field]((prevState) => ({
                ...prevState,
                value: e.target.value,
                validationText: ''
            }));
        }
    };
    const showGLDs = () => Object.values(accountConfig.EmpUDFConfig).length > 0;
    const generateGLDs = () => Object.entries(accountConfig.EmpUDFConfig).map(([key, value]) => {
        const gldMap = {
            1: [empGLD1Name, setEmpGLD1Name, setEmpGLD1PK, gld1, empGLD1PK],
            2: [empGLD2Name, setEmpGLD2Name, setEmpGLD2PK, gld2, empGLD2PK],
            3: [empGLD3Name, setEmpGLD3Name, setEmpGLD3PK, gld3, empGLD3PK],
            4: [empGLD4Name, setEmpGLD4Name, setEmpGLD4PK, gld4, empGLD4PK],
            5: [empGLD5Name, setEmpGLD5Name, setEmpGLD5PK, gld5, empGLD5PK]
        };
        if (value.UseSearch) {
            return (_jsx(EmpUDFSearch, { id: `GLD${key}`, label: value.Label, style: { width: '100%' }, value: new EmpUDF({ Name: gldMap[key][0], PK: gldMap[key][4].value || 0 }), onSelect: (val, reason) => {
                    if (reason === 'clear') {
                        gldMap[key][1]('');
                        gldMap[key][2]({ value: 0, validationText: '' });
                    }
                    else {
                        gldMap[key][1](val.Name);
                        gldMap[key][2]({ value: val.PK, validationText: '' });
                    }
                }, helperText: gldMap[key][4].validationText, error: !!gldMap[key][4].validationText, udfIndex: parseInt(key, 10) }, `GLD${key}`));
        }
        return (_jsx(Autocomplete, { id: `GLD${key}`, label: value.Label, style: { width: '100%' }, value: gldMap[key][0], options: gldMap[key][3], getOptionLabel: (option) => option?.value || option, isOptionEqualToValue: (option, val) => val === option.value, onChange: (e, val, reason) => {
                if (reason === 'clear') {
                    gldMap[key][1]('');
                    gldMap[key][2]({ value: 0, validationText: '' });
                }
                else {
                    gldMap[key][1](val.value);
                    gldMap[key][2]({ value: val.pk, validationText: '' });
                }
            }, helperText: gldMap[key][4].validationText, error: !!gldMap[key][4].validationText }, `GLD${key}`));
    });
    const setGLDValues = useCallback(() => Object.entries(accountConfig.EmpUDFConfig).forEach(([key, value]) => {
        if (open) {
            const gldMap = {
                1: [setEmpGLD1Name, setEmpGLD1PK, gld1, userData?.EmpGLD1Name],
                2: [setEmpGLD2Name, setEmpGLD2PK, gld2, userData?.EmpGLD2Name],
                3: [setEmpGLD3Name, setEmpGLD3PK, gld3, userData?.EmpGLD3Name],
                4: [setEmpGLD4Name, setEmpGLD4PK, gld4, userData?.EmpGLD4Name],
                5: [setEmpGLD5Name, setEmpGLD5PK, gld5, userData?.EmpGLD5Name]
            };
            if (value.Required &&
                !value.HasBlank &&
                !value.UseSearch &&
                !gldMap[key][3].length &&
                !userData.UserEmail) {
                const gld = gldMap[key][2];
                gldMap[key][0](gld[0].value);
                gldMap[key][1]({ value: gld[0].pk, validationText: '' });
            }
        }
    }), [accountConfig.EmpUDFConfig, gld1, gld2, gld3, gld4, gld5, open, userData]);
    useEffect(setGLDValues, [setGLDValues]);
    const cancelClickHandler = () => {
        setValidationResults([]);
        onCancel();
    };
    const checkValidation = () => {
        let valid = true;
        const required = [
            [firstName.value, setFirstName],
            [lastName.value, setLastName],
            [userEmail.value, setUserEmail],
            [departmentPK.value, setDepartmentPK],
            [role.value, setRole]
        ];
        if (accountConfig?.UIFlags.UseLockedWorkflow) {
            required.push([approverEmail.value, setApproverEmail]);
        }
        const gldMap = {
            1: [empGLD1PK.value, setEmpGLD1PK],
            2: [empGLD2PK.value, setEmpGLD2PK],
            3: [empGLD3PK.value, setEmpGLD3PK],
            4: [empGLD4PK.value, setEmpGLD4PK],
            5: [empGLD5PK.value, setEmpGLD5PK]
        };
        Object.entries(accountConfig.EmpUDFConfig).forEach(([key, value]) => {
            if (value.Required) {
                required.push(gldMap[key]);
            }
        });
        const newValidationResults = [];
        if (validateEmail(userEmail.value).includes('valid')) {
            setUserEmail((prevState) => ({
                ...prevState,
                validationText: validateEmail(userEmail.value)
            }));
            newValidationResults.push($t('Email address is invalid'));
            valid = false;
        }
        required.forEach(([value, setter]) => {
            if (!value || value.length === 0) {
                setter((prevState) => ({
                    ...prevState,
                    validationText: $t('This field is required')
                }));
                if (!newValidationResults.includes($t('Some required fields are empty.'))) {
                    newValidationResults.push($t('Some required fields are empty.'));
                }
                valid = false;
            }
        });
        setValidationResults(newValidationResults);
        return valid;
    };
    const scrollTop = () => {
        const element = document.getElementsByClassName('ErrorAlert')[0];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };
    const saveClickHandler = (e, location) => {
        const isValid = checkValidation();
        if (isValid) {
            setValidationResults([]);
            const userDetail = {
                UserPK: userData?.UserPK,
                UserEmail: userEmail.value,
                FirstName: firstName.value,
                LastName: lastName.value,
                EmpID: empID.value,
                EmailStatus: emailStatus.value,
                CommuteDistance: commuteDistance.value,
                CommuteUnits: commuteUnits.value,
                AdminLevel: adminLevel.value,
                AccountStatus: accountStatus.value,
                Role: role.value,
                Auditor: auditor.value,
                Treasurer: treasurer.value,
                Translator: translator.value,
                AccountsPayable: accountsPayable.value,
                DepartmentPK: departmentPK.value,
                DepartmentName: departmentName.value,
                DefaultCurrency: defaultCurrency.value,
                UseMultipleCurrencies: useMultipleCurrencies.value,
                UseVAT: useVAT.value,
                DefaultVATRate: defaultVATRate.value,
                UsePST: usePST.value,
                DefaultPSTRate: defaultPSTRate.value,
                UseHST: useHST.value,
                DefaultHSTRate: defaultHSTRate.value,
                ApproverPK: approverPK,
                ApproverEmail: approverEmail.value,
                AccountantPK: accountantPK,
                AccountantEmail: accountantEmail.value,
                AdditionalApproverPK: additionalApproverPK,
                AdditionalApproverEmail: additionalApproverEmail.value,
                ApproverForUsersCount: userData?.ApproverForUsersCount,
                AccountantForUsersCount: userData?.AccountantForUsersCount,
                ApprovalLimit: approvalLimit,
                AdministrativeNotes: administrativeNotes,
                DefaultSettlementAccount: defaultSettlementAccount,
                PayByCheck: payByCheck,
                EmpGLD1PK: empGLD1PK.value,
                EmpGLD1Name: empGLD1Name,
                EmpGLD2PK: empGLD2PK.value,
                EmpGLD2Name: empGLD2Name,
                EmpGLD3PK: empGLD3PK.value,
                EmpGLD3Name: empGLD3Name,
                EmpGLD4PK: empGLD4PK.value,
                EmpGLD4Name: empGLD4Name,
                EmpGLD5PK: empGLD5PK.value,
                EmpGLD5Name: empGLD5Name,
                DoNotSendWelcomeEmail: doNotSendWelcomeEmail
            };
            onSave(userDetail, location);
        }
        else {
            scrollTop();
        }
    };
    const onApproverShow = (type) => {
        if (type === 'Approver') {
            onApproverFor(userData.UserPK);
        }
        else {
            onAccountantFor(userData.UserPK);
        }
        onCancel();
    };
    const showApprovalLimit = () => approvalLimits.length > 0 &&
        (role.value === 'Manager' || role.value === 'Executive' || role.value === 'Accountant');
    const optionLabel = (option) => option.FullName?.length ? `${option.FullName} (${option.Email})` : option?.Email || '';
    const achAccount = () => {
        const hasAddress = !!userData?.ACHAccount?.CustomerAddress?.Address;
        const address = `${userData.ACHAccount.CustomerAddress.Address}, 
            ${userData.ACHAccount.CustomerAddress.City}, 
            ${userData.ACHAccount.CustomerAddress.State}`;
        const showAddress = accountConfig?.UIFlags.EnableCheckPayments && hasAddress && userData.PayByCheck;
        const bankInfo = `${userData.ACHAccount.CustomerBankID_Masked} ${userData.ACHAccount.CustomerBankAccount_Masked}`;
        const hasBankInfo = !/^[a-zA-Z]+$/.test(userData.ACHAccount.CustomerBankID_Masked);
        const content = () => {
            if (showAddress) {
                return address;
            }
            if (accountConfig?.UIFlags.EnableCheckPayments && !userData.PayByCheck && hasBankInfo) {
                return bankInfo;
            }
            if (!accountConfig?.UIFlags.EnableCheckPayments && hasBankInfo) {
                return bankInfo;
            }
            return $t('Add Account/Mailing Information');
        };
        const isReadOnly = userData.ACHAccount.ReadOnly_Flag.length > 0;
        return (_jsxs(Typography, { style: { marginTop: '8px' }, children: [_jsx(Link, { role: "button", onClick: isReadOnly ? () => { } : () => setOpenLeaveModal(true), color: isReadOnly ? 'textSecondary' : 'primary', underline: isReadOnly ? 'none' : 'always', style: { cursor: isReadOnly ? 'default' : 'pointer' }, children: content() }), isReadOnly && (_jsx(IconPopover, { title: $t('More information'), content: userData.ACHAccount.ReadOnly_Flag, iconUrl: SupportIcon, edge: false }))] }));
    };
    const getACHLink = () => {
        if (accountConfig?.UIFlags.ACHAccountPartner === 'PayrollExperts') {
            return `./EmpACHAccount.aspx?e=${userData.ACHAccount.LinkID}`;
        }
        if (accountConfig?.UIFlags.ACHAccountPartner === 'Convera') {
            if (accountConfig?.UIFlags.EnableCheckPayments && userData.PayByCheck) {
                return `./EmpCheckAddress.aspx?e=${userData.ACHAccount.LinkID}`;
            }
            return `./EmpWubsAccount.aspx?e=${userData.ACHAccount.LinkID}`;
        }
        return '';
    };
    const leaveModalConfirm = (e, save) => {
        const location = () => {
            window.open(maxOverrideLink || getACHLink(), '_blank');
        };
        if (save) {
            saveClickHandler(e, location);
            setMaxOverrideLink('');
        }
        else {
            location();
            closeDrawer();
            setMaxOverrideLink('');
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(ManageUsersLeaveModal, { open: openLeaveModal, onClose: () => {
                    setOpenLeaveModal(false);
                    setMaxOverrideLink('');
                }, onConfirm: leaveModalConfirm }), _jsx(ScrollableDrawer, { anchor: "right", open: open, sideWidth: currentBreakpoint.current === 'xs' ? 380 : 450, disableCloseOnBackdropClick: true, openUpdated: cancelClickHandler, header: $t('User Details'), footer: _jsxs(Stack, { direction: "row", justifyContent: "space-between", children: [_jsx(Button, { variant: "outlined", onClick: cancelClickHandler, children: $t('Cancel') }), _jsx(Button, { onClick: saveClickHandler, children: userData.UserEmail ? $t('Save Changes') : $t('Create User') })] }), children: _jsxs(Stack, { spacing: 3, direction: "column", alignItems: "stretch", justifyContent: "space-between", children: [validationResults.length > 0 && (_jsx(Alert, { className: "ErrorAlert", severity: "error", alertTitle: $t('Please fix the following:'), children: validationResults.map((item) => (_jsx(Typography, { children: item }, `${item.Key}`))) })), hasBillingIncrease && !userData.UserPK && (_jsxs(Alert, { severity: "warning", style: { width: 'fit-content' }, children: [_jsx(Typography, { variant: "body2", children: $t('Adding a user could result in a billing increase.') }), _jsx(Link, { href: "/CorpViewPlan.aspx", children: $t(' View your billing plan here') })] })), _jsx(Typography, { variant: "subtitle1", children: $t('General Settings') }), _jsx(Checkbox, { id: "AccountStatus", color: "primary", label: $t('Active Account'), checked: accountStatus.value, onChange: (e) => fieldChangeHandler(e, 'AccountStatus') }), _jsx(TextField, { id: "email", name: "Email", fullWidth: true, value: userEmail.value, label: $t('Email'), type: "text", helperText: userEmail.validationText, error: !!userEmail.validationText, inputProps: { autocomplete: 'one-time-code' }, onChange: (e) => fieldChangeHandler(e, 'UserEmail') }), _jsx(TextField, { id: "FirstName", name: "First Name", fullWidth: true, label: $t('First Name'), type: "text", value: firstName.value, helperText: firstName.validationText, error: !!firstName.validationText, inputProps: { autocomplete: 'one-time-code' }, onChange: (e) => fieldChangeHandler(e, 'FirstName') }), _jsx(TextField, { id: "LastName", name: "Last Name", fullWidth: true, label: $t('Last Name'), type: "text", value: lastName.value, helperText: lastName.validationText, error: !!lastName.validationText, inputProps: { autocomplete: 'one-time-code' }, onChange: (e) => fieldChangeHandler(e, 'LastName') }), _jsx(TextField, { id: "EmployeeID", name: "Employee ID", fullWidth: true, label: $t('Employee ID'), type: "text", value: empID.value, helperText: empID.validationText, error: !!empID.validationText, onChange: (e) => fieldChangeHandler(e, 'EmpID') }), accountConfig?.Emp.Admin &&
                            accountConfig?.UIFlags.EmpAdminLevel !== 'Lite' &&
                            userData.UserPK && (_jsx(Select, { id: "EmilStatus", style: { width: '100%', minWidth: 'auto' }, label: $t('Email Status'), value: emailStatus.value, helperText: emailStatus.validationText, error: !!emailStatus.validationText, onChange: (e) => fieldChangeHandler(e, 'EmailStatus'), children: emailStatuses.map((item) => (_jsx(MenuItem, { value: item.value, children: item.label }, item.label))) })), _jsxs(Stack, { spacing: 3, direction: "row", alignItems: "stretch", justifyContent: "space-between", children: [_jsx(TextField, { id: "CommuteDistance", name: "Commute Distance", fullWidth: true, label: $t('Commute Distance'), type: "number", value: commuteDistance.value, helperText: commuteDistance.validationText, error: !!commuteDistance.validationText, onChange: (e) => fieldChangeHandler(e, 'CommuteDistance') }), _jsx(Select, { id: "CommuteUnits", style: { width: '100%', minWidth: 'auto' }, label: $t('Commute Units'), value: commuteUnits.value, helperText: commuteUnits.validationText, error: !!commuteUnits.validationText, onChange: (e) => fieldChangeHandler(e, 'CommuteUnits'), children: commuteUnitsSelect.map((item) => (_jsx(MenuItem, { value: item.value, children: item.label }, item.label))) })] }), _jsx(Select, { id: "AdminLevel", style: { width: '100%', minWidth: 'auto' }, label: $t('Administration Level'), value: adminLevel.value, disabled: accountConfig?.UIFlags.EmpAdminLevel === 'Lite', helperText: adminLevel.validationText, error: !!adminLevel.validationText, onChange: (e) => fieldChangeHandler(e, 'AdminLevel'), children: adminLevels.map((item) => (_jsx(MenuItem, { value: item, children: item }, item))) }), _jsxs(Stack, { spacing: 0, direction: "row", children: [_jsx(Select, { id: "Role", style: { width: '100%', minWidth: 'auto' }, label: $t('Role'), value: role.value, helperText: role.validationText, error: !!role.validationText, onChange: (e) => fieldChangeHandler(e, 'Role'), children: empTypes().map((item) => (_jsx(MenuItem, { value: item.name, children: item.name }, item.name))) }), _jsx(IconPopover, { title: $t('More information'), content: _jsxs(_Fragment, { children: [_jsx(Typography, { children: $t(`All users must be assigned to one of four user roles. 
                                            Each role includes the capabilities of the previous role, plus others:`) }), _jsxs(Typography, { children: ["\u00A0 \u2022", _jsx("strong", { children: $t(' Employees ') }), $t("can create and submit expense reports, and update their account's configuration")] }), _jsxs(Typography, { children: ["\u00A0 \u2022", _jsx("strong", { children: $t(' Managers ') }), $t('can approve expense reports and submit them for reimbursement')] }), _jsxs(Typography, { children: ["\u00A0 \u2022", _jsx("strong", { children: $t(' Executives ') }), $t('can view company wide data while running reports')] }), _jsxs(Typography, { children: ["\u00A0 \u2022", _jsx("strong", { children: $t(' Accountants ') }), $t('are the final step in the approval process and have access to additional reporting tools')] }), _jsx(Spacer, { size: 10 }), _jsxs(Typography, { children: [$t('More information can be found '), _jsx(Link, { href: "https://help.certify.com/hc/en-us/articles/20504815654541", target: "_blank", children: $t('here') }), "."] })] }), iconUrl: SupportIcon, edge: false })] }), showApprovalLimit() && accountConfig.UIFlags.UseLockedWorkflow && (_jsx(Autocomplete, { id: "ApprovalLimit", label: "Approval Limit", style: { width: '100%' }, value: approvalLimit, options: approvalLimits, getOptionLabel: (option) => option.PK
                                ? `${option?.Name} ${option?.Amount} ${option?.CurrencySuffix}`
                                : '', onChange: (e, val, reason) => {
                                if (reason === 'clear') {
                                    setApprovalLimit(defaultApprovalLimit);
                                }
                                else {
                                    setApprovalLimit(val);
                                }
                            } })), _jsx(Checkbox, { id: "Auditor", color: "primary", label: $t('Auditor'), checked: auditor.value, onChange: (e) => fieldChangeHandler(e, 'Auditor') }), isTreasurerAvailable && (_jsx(Checkbox, { id: "Treasurer", color: "primary", label: $t('Treasurer'), checked: treasurer.value, onChange: (e) => fieldChangeHandler(e, 'Treasurer') })), accountConfig.UIFlags.UseInvoice && (_jsx(Checkbox, { id: "AccountsPayable", color: "primary", label: $t('Accounts Payable'), checked: accountsPayable.value, onChange: (e) => fieldChangeHandler(e, 'AccountsPayable') })), accountConfig.Company_UseMultipleLanguages && (_jsx(Checkbox, { id: "Translator", color: "primary", label: $t('Translator'), checked: translator.value, onChange: (e) => fieldChangeHandler(e, 'Translator') })), _jsx(Select, { id: "DepartmentName", style: { width: '100%', minWidth: 'auto' }, label: $t('Department'), value: departmentPK.value, helperText: departmentPK.validationText, error: !!departmentPK.validationText, onChange: (e, child) => {
                                setDepartmentPK({ value: child.props.value, validationText: '' });
                                setDepartmentName({ value: child.props.children, validationText: '' });
                            }, children: departments.map((item) => (_jsx(MenuItem, { value: item.PK, children: item.Name }, item.PK))) }), showGLDs() && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle1", children: $t('Employee Information') }), generateGLDs()] })), _jsx(Typography, { variant: "subtitle1", children: $t('Currency and Tax') }), _jsx(CurrencySelect, { id: "Currency", style: { width: '100%', minWidth: 'auto' }, label: $t('Default Currency'), value: defaultCurrency.value || 'USD', defaultValue: getCurrencyByCode(defaultCurrency.value || accountConfig?.Emp.CurrencyType.Suffix || 'USD'), options: currencies, helperText: defaultCurrency.validationText, error: !!defaultCurrency.validationText, onSelect: (currency, reason) => {
                                if (reason === 'clear') {
                                    setDefaultCurrency((prevState) => ({
                                        ...prevState,
                                        value: '',
                                        validationText: ''
                                    }));
                                }
                                else {
                                    setDefaultCurrency((prevState) => ({
                                        ...prevState,
                                        value: currency.Suffix,
                                        validationText: ''
                                    }));
                                }
                            } }), _jsx(Checkbox, { id: "UseMultipleCurrencies", color: "primary", label: $t('Use Multiple Currencies'), checked: useMultipleCurrencies.value, onChange: (e) => fieldChangeHandler(e, 'UseMultipleCurrencies') }), _jsx(Checkbox, { id: "UseVAT", color: "primary", label: $t('Use VAT'), checked: useVAT.value, onChange: (e) => fieldChangeHandler(e, 'UseVAT') }), !!useVAT.value && (_jsx(TextField, { id: "DefaultVATRate", name: "Default VAT Rate", fullWidth: true, label: $t('Default VAT Rate'), type: "number", value: defaultVATRate.value, helperText: defaultVATRate.validationText, error: !!defaultVATRate.validationText, onChange: (e) => fieldChangeHandler(e, 'DefaultVATRate') })), _jsx(Checkbox, { id: "UsePST", color: "primary", label: $t('Use PST'), checked: usePST.value, onChange: (e) => fieldChangeHandler(e, 'UsePST') }), !!usePST.value && (_jsx(TextField, { id: "DefaultPSTRate", name: "Default PST Rate", fullWidth: true, label: $t('Default PST Rate'), type: "number", value: defaultPSTRate.value, helperText: defaultPSTRate.validationText, error: !!defaultPSTRate.validationText, onChange: (e) => fieldChangeHandler(e, 'DefaultPSTRate') })), _jsx(Checkbox, { id: "UseHST", color: "primary", label: $t('Use HST'), checked: useHST.value, onChange: (e) => fieldChangeHandler(e, 'UseHST') }), !!useHST.value && (_jsx(TextField, { id: "DefaultHSTRate", name: "Default HST Rate", fullWidth: true, label: $t('Default HST Rate'), type: "number", value: defaultHSTRate.value, helperText: defaultHSTRate.validationText, error: !!defaultHSTRate.validationText, onChange: (e) => fieldChangeHandler(e, 'DefaultHSTRate') })), accountConfig.UIFlags.UseLockedWorkflow && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle1", children: $t('Approvals') }), _jsx(EmployeeSearch, { id: "ApproverEmail", label: $t('Approver Email'), style: { width: '100%' }, value: approver?.PK ? approver : null, helperText: approverEmail.validationText, error: !!approverEmail.validationText, empTypes: [EmpType.Manager, EmpType.Accountant, EmpType.Executive], getOptionLabel: optionLabel, userId: userData?.UserPK || null, onSelect: (employee, reason) => {
                                        if (reason === 'clear') {
                                            setApprover(new Employee());
                                            setApproverEmail({ value: '', validationText: '' });
                                            setApproverPK(0);
                                        }
                                        else {
                                            setApprover(employee);
                                            setApproverEmail({
                                                value: employee.Email,
                                                validationText: ''
                                            });
                                            setApproverPK(employee.PK);
                                        }
                                    } }), _jsx(EmployeeSearch, { id: "AccountantEmail", label: $t('Accountant Email'), style: { width: '100%' }, value: accountant?.PK ? accountant : null, helperText: accountantEmail.validationText, error: !!accountantEmail.validationText, empTypes: [EmpType.Accountant], getOptionLabel: optionLabel, onSelect: (employee, reason) => {
                                        if (reason === 'clear') {
                                            setAccountant(new Employee());
                                            setAccountantEmail({ value: '', validationText: '' });
                                            setAccountantPK(0);
                                        }
                                        else {
                                            setAccountant(employee);
                                            setAccountantEmail({
                                                value: employee.Email,
                                                validationText: ''
                                            });
                                            setAccountantPK(employee.PK);
                                        }
                                    } }), _jsx(EmployeeSearch, { id: "AdditionalApproverEmail", label: $t('2nd Approver Email'), style: { width: '100%' }, value: additionalApprover?.PK ? additionalApprover : null, helperText: additionalApproverEmail.validationText ||
                                        $t('Expense Reports are routed for second level approval based on policy settings.'), error: !!additionalApproverEmail.validationText, empTypes: [EmpType.Manager, EmpType.Accountant, EmpType.Executive], getOptionLabel: optionLabel, userId: userData?.UserPK || null, onSelect: (employee, reason) => {
                                        if (reason === 'clear') {
                                            setAdditionalApprover(new Employee());
                                            setAdditionalApproverEmail({
                                                value: '',
                                                validationText: ''
                                            });
                                            setAdditionalApproverPK(0);
                                        }
                                        else {
                                            setAdditionalApprover(employee);
                                            setAdditionalApproverEmail({
                                                value: employee.Email,
                                                validationText: ''
                                            });
                                            setAdditionalApproverPK(employee.PK);
                                        }
                                    } }), _jsxs(Stack, { spacing: 3, direction: "row", children: [_jsx(Chip, { label: $t(`Approver for ${userData?.ApproverForUsersCount || '0'} Users`), color: userData?.ApproverForUsersCount ? 'primary' : 'monochrome', variant: "outlined", onClick: () => userData?.ApproverForUsersCount
                                                ? onApproverShow('Approver')
                                                : null }), _jsx(Chip, { label: $t(`Accountant for ${userData?.AccountantForUsersCount || '0'} Users`), color: userData?.AccountantForUsersCount ? 'primary' : 'monochrome', variant: "outlined", onClick: () => userData?.AccountantForUsersCount
                                                ? onApproverShow('Accountant')
                                                : null })] })] })), (accountConfig.UIFlags.ACHAccountPartner === 'Convera' ||
                            accountConfig.UIFlags.ACHAccountPartner === 'PayrollExperts') && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle1", children: $t('Payments') }), achAccount(), accountConfig.UIFlags.ACHAccountPartner === 'Convera' && (_jsx(Select, { id: "DefaultSettlementAccount", style: { width: '100%', minWidth: 'auto' }, label: $t('Default Settlement Account'), value: !!defaultSettlementAccount && defaultSettlementAccount, onChange: (e) => setDefaultSettlementAccount(e.target.value), children: settlementAccounts.map((item) => (_jsx(MenuItem, { value: item.PK, children: item.Name }, item.PK))) })), accountConfig.UIFlags.ACHAccountPartner === 'Convera' &&
                                    accountConfig.UIFlags.EnableCheckPayments && (_jsx(Checkbox, { id: "PayByCheck", color: "primary", label: $t('Pay By Check'), checked: payByCheck, onChange: (e) => setPayByCheck(e.target.checked) }))] })), accountConfig?.Company_UseExpCatMaxOverride && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle1", children: $t('Max Override Rules') }), expCatMaxOverride.length > 0 &&
                                    expCatMaxOverride.map((item) => (_jsx(Typography, { style: { marginTop: '14px' }, children: _jsx(Link, { role: "button", onClick: () => {
                                                setOpenLeaveModal(true);
                                                setMaxOverrideLink(item.LinkID);
                                            }, children: item.ExpCatAmounts || $t('Add New') }) }, item.LinkID)))] })), !userData.UserPK && (_jsx(Checkbox, { id: "DoNotSendWelcomeEmail", color: "primary", label: $t('Do Not Send Welcome Email'), checked: doNotSendWelcomeEmail, onChange: (e) => setDoNotSendWelcomeEmail(e.target.checked), helpTextContent: _jsx(Typography, { children: $t(`Welcome emails provide users with their first-time login credentials. 
                                    You can choose to send these later on the Manage Users page.`) }) })), userData.UserPK && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle1", children: $t('Administrative Notes') }), _jsx(TextField, { id: "AdministrativeNotes", name: "Administrative Notes", multiline: true, fullWidth: true, defaultValue: administrativeNotes, rows: 6, onChange: (e) => setAdministrativeNotes(e.target.value), onBlur: (e) => setAdministrativeNotes(e.target.value) })] }))] }) })] }));
}
export default ManageUsersUserDrawer;
